// frontend/src/App.jsx
import { Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@clerk/clerk-react";
import PortalApp from "./portal/PortalApp.jsx";
import PortalAuthSync from "./components/PortalAuthSync.jsx";
import StaffGuard, { useStaffRole } from "./components/StaffGuard.jsx";

import Overview from "./pages/Overview.jsx";
import Intake from "./pages/Intake.jsx";
import RiskReview from "./pages/RiskReview.jsx";
import Export from "./pages/Export.jsx";
import CompanyPage from "./pages/Company.jsx";
import PersonPage from "./pages/Person.jsx";
import SearchPage from "./pages/Search.jsx";
import EditReview from "./pages/EditReview.jsx";

import "/src/index.css";
import AlertsPanel from "/src/components/AlertsPanel.jsx";
import ImpersonationBanner from "./components/ImpersonationBanner.jsx";
import { ImpersonationProvider } from "./hooks/useImpersonation.jsx";

import TrialWarningBanner from "./components/TrialWarningBanner.jsx";
import TrialExpiredModal from "./components/TrialExpiredModal.jsx";
import ContextHelpPanel from "./components/ContextHelpPanel.jsx";

const INTAKE_STORAGE_KEY = "intake_draft";
const REVIEW_STORAGE_KEY = "review_draft";

// Base URL for the portal app (dev + prod), derived from env
const isDev = import.meta.env.DEV;

// In dev, portal runs on the same Vite dev server at /portal
const DEV_PORTAL_BASE = "http://localhost:5173/portal";

// In prod, use the configured portal root domain (e.g. portal.cbcfx.site)
const PROD_PORTAL_BASE = import.meta.env.VITE_PORTAL_ROOT_DOMAIN ? `https://${import.meta.env.VITE_PORTAL_ROOT_DOMAIN}/portal` : "https://portal.cbcfx.site/portal";

const PORTAL_BASE_URL = (isDev ? DEV_PORTAL_BASE : PROD_PORTAL_BASE).replace(/\/+$/, "");

// Simple component to redirect CBCFX /ops routes to the portal
function OpsRedirect({ path }) {
  useEffect(() => {
    const target = `${PORTAL_BASE_URL}/${path}`;
    window.location.href = target;
  }, [path]);

  return (
    <div className="portal-loading">
      <p>Redirecting to portal…</p>
    </div>
  );
}

function StaffLayout() {
  const location = useLocation();
  const { role } = useStaffRole();

  // Check if user is super_admin
  const isSuperAdmin = role === "super_admin";

  useEffect(() => {
    // Clear intake draft whenever we leave Intake route
    if (!location.pathname.startsWith("/intake")) {
      sessionStorage.removeItem(INTAKE_STORAGE_KEY);
    }
    // Clear risk review draft whenever we leave Review route
    if (!location.pathname.startsWith("/review")) {
      sessionStorage.removeItem(REVIEW_STORAGE_KEY);
    }
  }, [location.pathname]);

  return (
    <>
      <TrialWarningBanner />
      <ImpersonationBanner />
      <nav className="topbar">
        <Link to="/">Search</Link>
        <Link to="/overview">Database Overview</Link>
        <Link to="/intake">Intake</Link>
        <Link to="/review">Review</Link>
        <Link to="/export">Export</Link>

        {/* Show ops links only for super_admin */}
        {isSuperAdmin && (
          <>
            {/* These go to local /ops routes, which immediately redirect to portal */}
            <Link to="/ops/tenants">Ops Tenants</Link>
            <Link to="/ops/audit">Ops Audit</Link>
          </>
        )}

        {/* Goes to portal domain (dev+prod via env) */}
        <a
          href={PORTAL_BASE_URL}
          style={{
            marginLeft: "1rem",
            borderLeft: "1px solid rgba(255, 255, 255, 0.2)",
            paddingLeft: "1rem",
            color: "inherit",
            textDecoration: "none",
          }}>
          📋 Portal
        </a>

        {/* Alerts lives in the topbar, right-aligned */}
        <div style={{ marginLeft: "auto" }}>
          <AlertsPanel />
        </div>
      </nav>

      <Routes>
        <Route path="/" element={<SearchPage />} />
        <Route path="/overview" element={<Overview />} />
        <Route path="/intake" element={<Intake />} />
        <Route path="/review" element={<RiskReview />} />
        <Route path="/reviews/:id/edit" element={<EditReview />} />
        <Route path="/export" element={<Export />} />
        <Route path="/company/:id" element={<CompanyPage />} />
        <Route path="/person/:id" element={<PersonPage />} />

        {/* CBCFX /ops routes → redirect to portal ops routes */}
        <Route path="/ops/tenants" element={<OpsRedirect path="ops/tenants" />} />
        <Route path="/ops/audit" element={<OpsRedirect path="ops/audit" />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <ContextHelpPanel />
    </>
  );
}

export default function App() {
  const clerkEnabled = Boolean(import.meta.env.VITE_CLERK_PUBLISHABLE_KEY);

  // ╔═══════════════════════════════════════════════════════════════════════╗
  // ║ NO CLERK MODE - Staff routes work without authentication             ║
  // ╚═══════════════════════════════════════════════════════════════════════╝
  if (!clerkEnabled) {
    return (
      <>
        <TrialExpiredModal />
        <Routes>
          <Route path="/portal/*" element={<PortalApp />} />
          <Route
            path="/*"
            element={
              <ImpersonationProvider>
                <StaffLayout />
              </ImpersonationProvider>
            }
          />
        </Routes>
      </>
    );
  }

  // ╔═══════════════════════════════════════════════════════════════════════╗
  // ║ CLERK ENABLED - Two separate authentication flows                    ║
  // ╚═══════════════════════════════════════════════════════════════════════╝

  function StaffRoutes() {
    const { isLoaded, isSignedIn } = useAuth();

    // Wait for Clerk to load
    if (!isLoaded) {
      return (
        <div className="portal-loading">
          <p>Loading...</p>
        </div>
      );
    }

    // If not signed in, redirect to portal domain
    if (!isSignedIn) {
      const currentUrl = window.location.href;
      const returnUrl = encodeURIComponent(currentUrl);
      const portalLoginUrl = `${PORTAL_BASE_URL}/login?returnUrl=${returnUrl}`;
      window.location.href = portalLoginUrl;
      return (
        <div className="portal-loading">
          <p>Redirecting to login...</p>
        </div>
      );
    }

    // User is signed in, show staff content with guard
    return (
      <StaffGuard>
        <ImpersonationProvider>
          <StaffLayout />
        </ImpersonationProvider>
      </StaffGuard>
    );
  }

  return (
    <>
      <TrialExpiredModal />
      <PortalAuthSync />
      <Routes>
        {/* Portal routes handle their own auth */}
        <Route path="/portal/*" element={<PortalApp />} />

        {/* Staff routes require Clerk auth + StaffGuard */}
        <Route path="/*" element={<StaffRoutes />} />
      </Routes>
    </>
  );
}
